/**
 * interviewIntelligence.js — deterministic v1
 *
 * Pure function. No randomness, no LLM, no network. Given the pack's
 * job row, the user's fit analysis (job_scores entry), the selected
 * Source CV, and the selected CV variant, produce a readiness summary
 * plus 8–12 interview questions and two supporting lists.
 *
 * Rules:
 *   • readinessScore starts at 70.
 *       +5 per strong fit area (cap +20 → 4 strengths).
 *       -5 per major gap          (cap -15 → 3 gaps).
 *       Clamped to [45, 92].
 *   • 8–12 questions; categories: Technical, Behavioural, Gap / Risk,
 *     Leadership, Company / Motivation, CV Challenge.
 *   • Likelihood ∈ { Very Likely, Likely, Possible }.
 *   • No fake company, no fake interviewer, no placeholder data.
 */

export const CATEGORIES = [
  'People & Presence',      // behavioural, HR, leadership, communication, stakeholder management
  'Commercial Judgement',   // business acumen, prioritisation, commercial reasoning
  'Role Expertise',         // technical, role-specific capability, hard skills, domain
  'Pressure Points',        // gaps, risks, transitions, likely challenge areas
  'Motivation & Fit',       // why this company, why this role, cultural fit
  'Track Record',           // CV challenge, proving claims, defending prior experience
];

export const LIKELIHOODS = ['Very Likely', 'Likely', 'Possible'];

// ── helpers ─────────────────────────────────────────────────────────

function normaliseList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean);
}

// RC 1.24.b.1 — strip internal IDs / slugs / timestamp suffixes from
// job titles so they never leak into user-facing question copy. We
// don't know every ingestion pattern ingressed into the `jobs` table,
// so we defend by pattern: long numeric tails, hyphen+alphanumeric
// tails with digits, and obvious internal-ledger suffixes. If the
// cleaned result looks too short to be a real title, we drop it
// entirely and the callers fall back to "this role".
export function cleanJobTitle(job) {
  const raw = (job?.title || '').trim();
  if (!raw) return '';
  let cleaned = raw;
  // Remove long numeric tails ("- 1776764362679").
  cleaned = cleaned.replace(/\s*[-_]\s*\d{6,}\s*$/g, '').trim();
  // Remove alphanumeric slugs with digits ("-abc123def9").
  cleaned = cleaned.replace(/\s*[-_]\s*[a-z0-9]*\d[a-z0-9]*\s*$/gi, '').trim();
  // Collapse whitespace.
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  // Drop obvious internal-ledger phrasings that aren't role titles.
  if (/^[A-Z][a-z]+\s+(ownership|record|draft|ledger)$/.test(cleaned)) {
    return '';
  }
  // If what's left has fewer than 3 letters, it's garbage.
  const letters = (cleaned.match(/[A-Za-z]/g) || []).length;
  if (letters < 3) return '';
  return cleaned;
}

// Public role label — clean title when possible, else company + "role",
// else the generic fallback. Never leaks raw slugs.
export function jobLabel(job, fallback = 'this role') {
  const t = cleanJobTitle(job);
  if (t) return t;
  const company = (job?.company || '').trim();
  if (company) return `${company} role`;
  return fallback;
}

// RC 1.24.b.1 — tidy common awkward LLM phrasings in gap / strength
// strings before we embed them in user-facing question copy. We keep
// this deliberately conservative: the point is to fix known artefacts
// (e.g. "weak articulating of fit in cover letter"), not to second-
// guess the model's reasoning. Exported so FitResultCard can apply
// the same tidy pass to the raw strengths / risks list it renders.
export function cleanPhrase(raw) {
  if (!raw) return '';
  let s = String(raw).replace(/^\W+/, '').trim();
  s = s.replace(/\bweak articulating\b/gi, 'weak articulation');
  s = s.replace(/\barticulating of\b/gi, 'articulation of');
  s = s.replace(/\bin cover letter\b/gi, 'in the cover letter');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

function detectSeniority(job) {
  const text = `${job?.seniority_level || ''} ${job?.title || ''}`.toLowerCase();
  if (/\b(chief|cxo|cto|cfo|ceo|vp|vice president|svp|head of|director|principal)\b/.test(text)) return 'senior';
  if (/\b(manager|lead|senior manager|associate director)\b/.test(text)) return 'manager';
  if (/\b(senior|sr\.?|staff)\b/.test(text)) return 'senior_ic';
  return 'ic';
}

function mentionsStakeholder(strengths) {
  const s = strengths.join(' ').toLowerCase();
  return /\b(stakeholder|communication|leadership|negotiation|cross[- ]functional)\b/.test(s);
}

function detectTransition(job, selectedVariant, risks) {
  const riskText = risks.join(' ').toLowerCase();
  if (/\b(transition|pivot|career change|industry change|unfamiliar|limited exposure|no direct)\b/.test(riskText)) return true;
  const varFamily = (selectedVariant?.role_family || '').toLowerCase();
  const title = (job?.title || '').toLowerCase();
  if (!varFamily || !title) return false;
  const varTokens = varFamily.split(/[^a-z]+/).filter((t) => t.length >= 4);
  if (varTokens.length === 0) return false;
  return !varTokens.some((t) => title.includes(t));
}

function cleanFirst(list, fallback) {
  const first = list.find(Boolean);
  return (first && first.trim()) || fallback;
}

// ── question templates ──────────────────────────────────────────────
// Every template returns { question, category, likelihood, why,
// include:[], mistakes:[], followUp }. Callers fill in id.

function gapQuestion(gap, { jobTitle }) {
  const gapShort = cleanPhrase(gap).slice(0, 160);
  const role = jobTitle || 'this role';
  return {
    question: `You have limited direct exposure to ${gapShort.toLowerCase()}. Why do you believe you are ready for ${/^a |^an |^the /i.test(role) ? role : `a ${role}`} that requires it?`,
    category: 'Pressure Points',
    likelihood: 'Very Likely',
    why: 'To test whether you can bridge a visible experience gap rather than avoid it. Interviewers expect a measured, evidence-based answer.',
    include: [
      'Transferable skills from adjacent work',
      'The closest analogue in your own track record',
      'Concrete evidence (numbers, outcomes, scope)',
      'Why this role is a logical next step rather than a leap',
    ],
    mistakes: [
      'Pretending the gap does not exist',
      'Becoming defensive or dismissive of the concern',
      'Over-claiming experience you do not actually have',
    ],
    followUp: 'Can you give a specific example where you operated close to this area and what you learned?',
  };
}

function strengthTechnicalQuestion(strength) {
  const strengthShort = cleanPhrase(strength);
  return {
    question: `Walk us through the most technically or commercially demanding piece of work you have led involving ${strengthShort.toLowerCase()}.`,
    category: 'Role Expertise',
    likelihood: 'Very Likely',
    why: 'To validate that the strength on paper holds up under structured questioning. This is where you earn or lose credibility.',
    include: [
      'The business problem and why it mattered',
      'The approach you chose and the trade-offs you weighed',
      'Quantified outcomes (time, money, risk, scope)',
      'What you would do differently now',
    ],
    mistakes: [
      'Narrating only the happy path',
      'Claiming team wins as personal ones',
      'Skipping the numbers',
    ],
    followUp: 'What was the single decision in that project that had the largest impact on the outcome?',
  };
}

function behaviouralStakeholderQuestion() {
  return {
    question: 'Tell us about a time you had to defend an unpopular recommendation to senior stakeholders. What did you do, and how did it land?',
    category: 'People & Presence',
    likelihood: 'Very Likely',
    why: 'Your CV emphasises stakeholder management. They will test whether that means *influencing* or merely *accommodating*.',
    include: [
      'The stakeholder map and the specific dissent',
      'How you framed the recommendation',
      'The data or argument you anchored on',
      'The eventual decision and its downstream effect',
    ],
    mistakes: [
      'A story where you simply went along with seniority',
      'Vague descriptors ("the team", "the business")',
      'No mention of the human dynamics at play',
    ],
    followUp: 'If the decision had gone the other way, what would your plan B have been?',
  };
}

function leadershipQuestion() {
  return {
    question: 'Describe a time you had to raise the performance bar of a team that had become comfortable with the status quo. How did you approach it?',
    category: 'People & Presence',
    likelihood: 'Likely',
    why: 'For manager+ roles, interviewers want to see you can change a team, not just operate one.',
    include: [
      'The diagnosis — what was specifically off',
      'The intervention sequence (not a single heroic act)',
      'How you handled resistance',
      'How you measured the change',
    ],
    mistakes: [
      'Framing it as a personality clash',
      'Skipping the measurement step',
      'Claiming a cultural turnaround in a few weeks',
    ],
    followUp: 'What did that experience change about how you now hire?',
  };
}

function companyMotivationQuestion(company) {
  return {
    question: `Why ${company} specifically, and why now?`,
    category: 'Motivation & Fit',
    likelihood: 'Likely',
    why: 'This is the filter for candidates who are running *towards* this role vs. away from their current one.',
    include: [
      'A specific, verifiable reason grounded in the company\'s recent direction',
      'How your trajectory fits the next 3–5 years there',
      'A candid note on what you cannot get in your current seat',
    ],
    mistakes: [
      'Generic praise that would fit any employer',
      'Criticising your current employer',
      'Pretending the compensation piece does not exist',
    ],
    followUp: 'What is one thing about us that you think we get wrong?',
  };
}

function cvChallengeQuestion(jobTitle) {
  const role = (jobTitle || '').trim();
  // jobRoleLabel may already end in " role" (from the company-fallback
  // branch of jobLabel). Don't double it up.
  const tail = /\brole\s*$/i.test(role) ? role : `${role || 'this'} role`;
  const article = /^(a |an |the )/i.test(tail) ? '' : 'a ';
  return {
    question: `Your background is not the most obvious match for ${article}${tail}. Walk us through the logic of this move.`,
    category: 'Track Record',
    likelihood: 'Very Likely',
    why: 'When the CV does not read as an exact fit, interviewers look for narrative coherence. They want the "why" behind the jump.',
    include: [
      'The through-line across your roles (skills, domains, scale)',
      'The deliberate bets you have already made in this direction',
      'Evidence that you have derisked the transition',
    ],
    mistakes: [
      'Apologising for your background',
      'Rewriting the past to sound more linear than it was',
      'Relying on "I\'m a fast learner" as the load-bearing claim',
    ],
    followUp: 'Who have you spoken to in this space to pressure-test the move?',
  };
}

// RC 1.24.b — surfaced when the cover letter is weak (<70). The fit
// analysis explicitly flagged the letter as not carrying its weight,
// so interviewers will probe motivation rather than accept the
// written narrative.
function coverLetterMotivationQuestion({ jobTitle, company, letterSub }) {
  const where = company ? `${company}` : 'us';
  const roleLabel = jobTitle || 'role';
  return {
    question: `Take two minutes — no script — and tell us why ${where}, why this ${roleLabel}, and why now. Convince us.`,
    category: 'Motivation & Fit',
    likelihood: 'Very Likely',
    why: `Your cover letter scored ${letterSub ?? 'low'}/100 — the written narrative wasn't clearly connecting your background to the role, so expect the interviewer to press you verbally for the specific, ungeneric reason.`,
    include: [
      'A concrete reason tied to this company, not the sector',
      'A specific thing you want that your current seat cannot give you',
      'One piece of evidence that this is a planned move, not opportunistic',
    ],
    mistakes: [
      'Recycling the cover letter verbatim',
      'Generic praise ("strong brand", "great culture")',
      'Framing it primarily as compensation or location',
    ],
    followUp: 'What would make you turn us down if we offered?',
  };
}

// RC 1.24.b — commercial judgement slot. Seeded from a strength
// (preferred) or a gap, so every pack surfaces at least one
// business-reasoning question rather than the pure behavioural /
// technical mix we had before.
function commercialJudgementQuestion({ strength, gap, jobTitle }) {
  const seed = cleanPhrase(strength || gap || '');
  if (seed) {
    const shortSeed = seed.slice(0, 140);
    return {
      question: `Walk us through a time when ${shortSeed.toLowerCase()} translated into a measurable commercial outcome. What did you trade off to get it?`,
      category: 'Commercial Judgement',
      likelihood: 'Likely',
      why: 'They want to see that your expertise connects to revenue, margin, or risk — not just effort. The trade-off question separates operators from specialists.',
      include: [
        'The commercial frame (revenue, cost, risk, or opportunity)',
        'The option you chose and the option you rejected',
        'The quantified outcome — and the lag on that outcome',
      ],
      mistakes: [
        'A functionally detailed story with no commercial frame',
        'Claiming commercial impact you cannot size',
        'Skipping the second-order effects',
      ],
      followUp: 'If you ran it again with today\'s information, what would you change?',
    };
  }
  const roleLabel = jobTitle || 'role like this';
  return {
    question: `Talk us through how you would prioritise your first three commercial wins in ${/^a |^an |^the /i.test(roleLabel) ? roleLabel : `a ${roleLabel}`}.`,
    category: 'Commercial Judgement',
    likelihood: 'Likely',
    why: 'Commercial prioritisation under ambiguity — this question is how they distinguish strategic thinkers from reactive executors.',
    include: [
      'A criterion for what counts as a "win"',
      'The one you would start with and why',
      'How you would know you were wrong',
    ],
    mistakes: [
      'Listing activities instead of outcomes',
      'Assuming unlimited headcount or budget',
      'No mention of how you would measure it',
    ],
    followUp: 'Which of those three is most politically difficult, and why?',
  };
}

function riskMitigationQuestion(gap) {
  const short = cleanPhrase(gap);
  return {
    question: `How would you plan your first 90 days to close the gap around ${short.toLowerCase()}?`,
    category: 'Pressure Points',
    likelihood: 'Likely',
    why: 'They want to see whether you have thought operationally about the gap, not just philosophically.',
    include: [
      'The first 30 days — listening and mapping',
      'The next 30 days — targeted learning and relationship-building',
      'The final 30 days — a concrete, measurable output',
    ],
    mistakes: [
      'A vague "I\'ll ramp up quickly" answer',
      'Promising a big win in 30 days',
      'Ignoring internal stakeholders in the plan',
    ],
    followUp: 'Who would you want as your first two allies inside the organisation, and why?',
  };
}

function secondTechnicalDepthQuestion(strength) {
  const s = cleanPhrase(strength);
  return {
    question: `Pick a hard trade-off you made involving ${s.toLowerCase()} where reasonable people would have disagreed with you. Talk us through it.`,
    category: 'Role Expertise',
    likelihood: 'Likely',
    why: 'Trade-off questions separate strong operators from polished storytellers. They want to see the *shape* of your judgement.',
    include: [
      'Both options, fairly characterised',
      'What data / intuition tilted the decision',
      'The second-order effects you anticipated',
      'What actually happened',
    ],
    mistakes: [
      'Presenting the chosen option as obviously correct',
      'Skipping the cost of the road not taken',
      'No quantified follow-through',
    ],
    followUp: 'Knowing what you know now, would you still make the same call?',
  };
}

// ── public API ──────────────────────────────────────────────────────

export function buildInterviewIntelligence({
  job,
  fitResult,
  sourceCv,
  selectedVariant,
}) {
  if (!job || !fitResult) {
    return {
      available: false,
      reason: 'Interview Intelligence will appear after a fit analysis has been completed for this application.',
    };
  }

  const reasoning = fitResult.reasoning_json || {};
  const strengths = normaliseList(reasoning.key_strengths).map(cleanPhrase);
  const gaps      = normaliseList(reasoning.key_risks).map(cleanPhrase);
  const summaryIn = (reasoning.reasoning_summary || '').trim();
  const jobRoleLabel = jobLabel(job);

  // RC 1.24.b — cover-letter-aware inputs. Each is optional; the
  // builder degrades cleanly to RC 1.22 behaviour when they are
  // absent (legacy score rows).
  const overallFit   = Number.isFinite(Number(reasoning.overall_fit_score))
    ? Number(reasoning.overall_fit_score)
    : null;
  const cvSub        = Number.isFinite(Number(reasoning.cv_sub_score))
    ? Number(reasoning.cv_sub_score)
    : null;
  const letterSub    = Number.isFinite(Number(reasoning.cover_letter_sub_score))
    ? Number(reasoning.cover_letter_sub_score)
    : null;
  const isTransition = reasoning.is_transition_role === true;
  const boundsApplied = Array.isArray(reasoning.bounds_applied)
    ? reasoning.bounds_applied
    : [];
  const scoreExplanation = (reasoning.score_explanation || '').trim();
  const letterSent  = reasoning.letter_sent_to_ndor === true;

  // ── readiness ──────────────────────────────────────────────────
  // If NDOR gave us an overall fit, anchor readiness to it (clamped
  // to the 45-92 band). Otherwise fall back to the RC 1.22 template
  // formula so legacy packs still produce a sensible number.
  let readiness;
  if (overallFit != null) {
    readiness = Math.max(45, Math.min(92, Math.round(overallFit)));
  } else {
    readiness = 70 + Math.min(strengths.length, 4) * 5 - Math.min(gaps.length, 3) * 5;
    readiness = Math.max(45, Math.min(92, readiness));
  }

  const strongestArea = cleanFirst(strengths, 'Evidence from your current role');
  const biggestRisk   = cleanFirst(gaps,      'Direct experience signal in the target domain');

  // Summary blend: prefer the RC 1.24.b score_explanation when it
  // exists (it reflects the cover-letter-aware reasoning and any
  // sanity caps), then layer on the strongest-area / biggest-risk
  // sentence so the candidate still gets an actionable frame.
  const capNote = boundsApplied.length > 0
    ? ` The fit was capped — prepare to be pressed on the reasons.`
    : '';
  const baseSummary = (strengths.length === 0 && gaps.length === 0)
    ? (summaryIn || 'Your fit analysis did not surface explicit strengths or gaps. The preparation set below focuses on the role fundamentals.')
    : `You are likely to perform strongly on questions that test ${strongestArea.toLowerCase()}, but should prepare specifically for questions about ${biggestRisk.toLowerCase()} and role transition.`;
  const summary = scoreExplanation
    ? `${scoreExplanation}${capNote} ${baseSummary}`
    : `${baseSummary}${capNote}`;

  // ── signals ────────────────────────────────────────────────────
  const seniority  = detectSeniority(job);
  const hasCompany = !!(job.company && job.company.trim());
  // RC 1.24.b — prefer NDOR's explicit transition flag; fall back
  // to the heuristic for legacy rows.
  const transition = isTransition || detectTransition(job, selectedVariant, gaps);
  const stakeholderSignal = mentionsStakeholder(strengths);
  const weakLetter = letterSent && letterSub != null && letterSub < 70;

  // ── questions ──────────────────────────────────────────────────
  const qs = [];

  // 1-2 Gap / Risk questions
  if (gaps[0]) qs.push(gapQuestion(gaps[0], { jobTitle: jobRoleLabel }));
  if (gaps[1]) qs.push(riskMitigationQuestion(gaps[1]));
  else if (gaps[0]) qs.push(riskMitigationQuestion(gaps[0]));

  // 1 Technical/Leadership from strongest
  if (strengths[0]) qs.push(strengthTechnicalQuestion(strengths[0]));

  // RC 1.24.b — Commercial Judgement slot. Always present. Seeded
  // from the top strength (preferred) or top gap.
  qs.push(commercialJudgementQuestion({
    strength: strengths[0],
    gap: gaps[0],
    jobTitle: jobRoleLabel,
  }));

  // RC 1.24.b — if the cover letter was weak, surface a pointed
  // Motivation & Fit question that names the weakness and forces
  // a live defence of the motivation.
  if (weakLetter) {
    qs.push(coverLetterMotivationQuestion({
      jobTitle: jobRoleLabel,
      company: job.company,
      letterSub,
    }));
  }

  // stakeholder behavioural
  if (stakeholderSignal) qs.push(behaviouralStakeholderQuestion());
  else qs.push({
    question: 'Describe a project where the outcome depended as much on people as on the analysis. How did you navigate it?',
    category: 'People & Presence',
    likelihood: 'Likely',
    why: 'Most interviewers do not test competence in isolation — they test it under the messiness of real teams.',
    include: [
      'The people dynamics you read early',
      'The choice point where the human layer nearly derailed the work',
      'What you actively did, not just observed',
    ],
    mistakes: [
      'A purely technical retelling',
      'Framing yourself as the lone adult in the room',
    ],
    followUp: 'What did you learn about yourself from that project?',
  });

  // leadership if manager+
  if (seniority === 'manager' || seniority === 'senior') qs.push(leadershipQuestion());

  // company / motivation
  if (hasCompany) qs.push(companyMotivationQuestion(job.company));

  // CV challenge if transition
  if (transition) qs.push(cvChallengeQuestion(jobRoleLabel));

  // second technical depth for a rounded set
  if (strengths[1]) qs.push(secondTechnicalDepthQuestion(strengths[1]));
  else if (strengths[0]) qs.push(secondTechnicalDepthQuestion(strengths[0]));

  // Pad to minimum 8 with genuinely useful fundamentals.
  const padTemplates = [
    {
      question: `What does a successful first year in this ${jobRoleLabel} look like to you?`,
      category: 'People & Presence',
      likelihood: 'Likely',
      why: 'It tests whether you have translated the job spec into a measurable plan or are still operating at the level of generic ambition.',
      include: [
        'Two or three concrete outputs, not activities',
        'What you would need from your manager',
        'What you would expect to know by month 6 that you do not know now',
      ],
      mistakes: ['Listing activities instead of outcomes', 'No mention of what support you need'],
      followUp: 'Which of those outcomes is the hardest, and why?',
    },
    {
      question: 'Tell us about the last time you changed your mind on something important at work. What moved you?',
      category: 'People & Presence',
      likelihood: 'Possible',
      why: 'A quiet but revealing question about intellectual honesty. Strong candidates have concrete examples; weak candidates reach for generic ones.',
      include: ['The original position', 'The data, argument, or person that shifted you', 'What you did differently as a result'],
      mistakes: ['Trivial examples (coffee orders)', 'Framing the change as strategic flexibility rather than a real update'],
      followUp: 'What belief are you still holding that you suspect you might be wrong about?',
    },
    {
      question: 'What is the question you are hoping we do not ask?',
      category: 'People & Presence',
      likelihood: 'Possible',
      why: 'Used sparingly, but it rewards self-awareness. The answer signals whether you know your own weak spots.',
      include: ['A real, non-trivial soft spot', 'How you are already addressing it', 'Why it is not a reason not to hire you'],
      mistakes: ['A humblebrag ("I care too much")', 'A disqualifying confession'],
      followUp: 'If we asked you about that now, what would you say?',
    },
  ];
  let padIdx = 0;
  while (qs.length < 8 && padIdx < padTemplates.length) {
    qs.push(padTemplates[padIdx]);
    padIdx += 1;
  }

  // Hard cap at 12 to respect the spec.
  const questions = qs.slice(0, 12).map((q, i) => ({ id: `q-${i + 1}`, ...q }));

  // ── challenge areas ────────────────────────────────────────────
  const challengeAreas = gaps.slice(0, 4).map((g) => ({
    issue: g,
    why: 'Flagged as a key risk in your fit analysis. Expect targeted questioning here.',
    how: 'Prepare a 60-second answer that names the gap, cites the closest analogue in your experience, and outlines your first-90-days plan to close it.',
  }));
  // Always give the candidate something actionable.
  if (challengeAreas.length < 2) {
    challengeAreas.push({
      issue: 'Specificity of examples under pressure',
      why: 'Under time pressure, candidates often retreat to generalities. Interviewers read generality as weak evidence.',
      how: 'Pre-rehearse three STAR-format stories that collectively cover technical depth, leadership under ambiguity, and a commercial outcome.',
    });
  }
  if (challengeAreas.length < 2 && cleanJobTitle(job)) {
    challengeAreas.push({
      issue: `Articulating the "why this ${jobRoleLabel}" story`,
      why: 'Interviewers rank motivation almost as highly as competence. A thin motivation answer caps your overall score.',
      how: 'Write three sentences that link the role to your next 3–5 years. Rehearse until they feel unforced.',
    });
  }

  // ── selling points ─────────────────────────────────────────────
  const sellingPoints = strengths.slice(0, 5).map((s) => ({
    point: s,
    reinforce: 'Return to this point in at least two answers so the interviewer hears it as a theme, not a one-off claim.',
  }));
  if (sellingPoints.length < 3) {
    if (sourceCv?.original_filename) {
      sellingPoints.push({
        point: 'The evidence already in your Source CV',
        reinforce: 'Name specific achievements from the CV rather than rephrasing the bullet points in the abstract.',
      });
    }
    if (selectedVariant?.variant_name || selectedVariant?.role_family) {
      sellingPoints.push({
        point: `Alignment between your tailored "${selectedVariant.variant_name || selectedVariant.role_family}" variant and the role`,
        reinforce: 'Reference the tailoring decisions you made so it reads as deliberate, not cosmetic.',
      });
    }
    if (sellingPoints.length < 3) {
      sellingPoints.push({
        point: 'A clear, non-generic reason for wanting this specific role',
        reinforce: 'Land this reason inside the first two answers; it frames every later answer more favourably.',
      });
    }
  }

  return {
    available: true,
    readinessScore: readiness,
    strongestArea,
    biggestRisk,
    summary,
    questions,
    challengeAreas: challengeAreas.slice(0, 4),
    sellingPoints: sellingPoints.slice(0, 5),
  };
}

export default buildInterviewIntelligence;
